import type { HovTimBalanceState } from "./hoverIdleState";

export type HovTimBalanceTarget = "hov" | "tim" | "hovGain" | "timGain";

export type HovTimBalancePair = "resource" | "gain";

type HovTimAllocationKey =
  | "hovAllocation"
  | "timAllocation"
  | "hovGainAllocation"
  | "timGainAllocation";

export interface HovTimGrowthProgressResult {
  readonly gainedPoints: number;
  readonly nextProgress: number;
}

function hovTimBalancePairKeys(
  pair: HovTimBalancePair,
): readonly [HovTimAllocationKey, HovTimAllocationKey] {
  return pair === "resource"
    ? ["hovAllocation", "timAllocation"]
    : ["hovGainAllocation", "timGainAllocation"];
}

function allocationKeyOf(target: HovTimBalanceTarget): HovTimAllocationKey {
  switch (target) {
    case "hov":
      return "hovAllocation";
    case "tim":
      return "timAllocation";
    case "hovGain":
      return "hovGainAllocation";
    case "timGain":
      return "timGainAllocation";
  }
}

export function assignHovTimBalancePoint(
  balance: HovTimBalanceState,
  target: HovTimBalanceTarget,
): HovTimBalanceState | null {
  if (balance.unassigned <= 0) return null;
  const key = allocationKeyOf(target);
  return {
    ...balance,
    unassigned: balance.unassigned - 1,
    [key]: balance[key] + 1,
  };
}

export function unassignHovTimBalancePoint(
  balance: HovTimBalanceState,
  target: HovTimBalanceTarget,
): HovTimBalanceState | null {
  const key = allocationKeyOf(target);
  if (balance[key] <= 0) return null;
  return {
    ...balance,
    unassigned: balance.unassigned + 1,
    [key]: balance[key] - 1,
  };
}

export function setHovTimBalancePairRatio(
  balance: HovTimBalanceState,
  pair: HovTimBalancePair,
  ratio: number,
): HovTimBalanceState | null {
  const [leftKey, rightKey] = hovTimBalancePairKeys(pair);
  const total = balance[leftKey] + balance[rightKey];
  if (total <= 0) return null;
  const clamped = Number.isNaN(ratio) ? 0.5 : Math.max(0, Math.min(1, ratio));
  const right = Math.round(clamped * total);
  const left = total - right;
  if (left === balance[leftKey] && right === balance[rightKey]) {
    return null;
  }
  return {
    ...balance,
    [leftKey]: left,
    [rightKey]: right,
  };
}

export function addUnassignedHovTimBalancePoints(
  balance: HovTimBalanceState,
  gainedPoints: number,
): HovTimBalanceState {
  if (gainedPoints <= 0) return balance;
  return {
    ...balance,
    total: balance.total + gainedPoints,
    unassigned: balance.unassigned + gainedPoints,
  };
}

export function advanceHovTimGrowthProgress(input: {
  readonly currentProgress: number;
  readonly gainPerSecond: number;
  readonly dtSeconds: number;
}): HovTimGrowthProgressResult {
  const nextProgress =
    input.currentProgress + input.gainPerSecond * input.dtSeconds;
  const gainedPoints = Math.floor(nextProgress);
  return {
    gainedPoints,
    nextProgress: nextProgress - gainedPoints,
  };
}
